/**
 * Copy to Clipboard Utilities
 * 
 * Provides hybrid copy functionality that:
 * - Preserves text as editable HTML/markdown
 * - Converts custom React components to embedded images
 * - Works seamlessly with Google Docs, Word, and other rich text editors
 */

import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';

interface CopyOptions {
  messageContainer: HTMLElement;
  cleanedContent: string;
  markdownComponents: any;
}

interface ComponentCapture {
  element: Element;
  dataURL?: string;
}

/**
 * Main function to copy content with hybrid approach
 */
export async function copyContentToClipboard({
  messageContainer,
  cleanedContent,
  markdownComponents
}: CopyOptions): Promise<boolean> {
  try {
    console.log('Starting hybrid copy process...');

    // Step 1: Find custom components in the DOM
    const foundComponents = findCustomComponents(messageContainer);
    console.log(`Found ${foundComponents.length} custom components to screenshot`);

    // Step 2: Screenshot each custom component
    if (foundComponents.length > 0) {
      await captureComponentImages(foundComponents);
    }

    // Step 3: Create hybrid HTML
    const hybridHTML = createHybridHTML(messageContainer, foundComponents, cleanedContent);

    // Step 4: Copy to clipboard with both HTML and plain text
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([hybridHTML], { type: 'text/html' }),
      'text/plain': new Blob([cleanedContent], { type: 'text/plain' })
    });

    await navigator.clipboard.write([clipboardItem]);
    console.log('Successfully copied hybrid content to clipboard');
    return true;
  } catch (err) {
    console.error('Failed to copy hybrid content:', err);
    // Fallback to basic HTML copy
    return copyAsBasicHTML(cleanedContent, markdownComponents);
  }
}

/**
 * Find custom components in the DOM that should be converted to images
 */
function findCustomComponents(container: HTMLElement): ComponentCapture[] {
  const customComponentSelectors = [
    '.highlight', // highlight components
    '[data-dynamic-ui]', // dynamic-ui components
    'svg:not(.copy-button svg)', // SVG elements (except copy button)
    '.alert', // alert components
    '[class*="network"]', // network components
    '[class*="plot"]' // plot components
  ];

  const foundComponents: ComponentCapture[] = [];

  for (const selector of customComponentSelectors) {
    const elements = container.querySelectorAll(selector);
    elements.forEach(el => {
      // Don't include nested elements or copy button
      if (!el.closest('.copy-button') && !foundComponents.some(comp => comp.element.contains(el) || el.contains(comp.element))) {
        foundComponents.push({ element: el });
        console.log(`Found custom component: ${selector}`, el);
      }
    });
  }

  return foundComponents;
}

/**
 * Capture screenshots of custom components
 */
async function captureComponentImages(components: ComponentCapture[]): Promise<void> {
  const { default: html2canvas } = await import('html2canvas');

  for (const component of components) {
    try {
      console.log('Screenshotting component:', component.element);

      // Create a temporary container to isolate the component
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.top = '-9999px';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.padding = '16px';
      tempDiv.style.border = '1px solid #e5e7eb';
      tempDiv.style.borderRadius = '12px';
      tempDiv.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
      tempDiv.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      tempDiv.style.fontSize = '14px';
      tempDiv.style.lineHeight = '1.5';

      // Clone the component
      const clonedComponent = component.element.cloneNode(true) as HTMLElement;

      // Copy computed styles from the original element to preserve appearance
      const originalStyles = window.getComputedStyle(component.element as HTMLElement);
      const stylesToCopy = [
        'backgroundColor', 'color', 'fontSize', 'fontFamily', 'fontWeight',
        'lineHeight', 'padding', 'margin', 'border', 'borderRadius',
        'boxShadow', 'display', 'flexDirection', 'alignItems', 'justifyContent'
      ];

      stylesToCopy.forEach(prop => {
        (clonedComponent as HTMLElement).style[prop as any] = originalStyles[prop as any];
      });

      tempDiv.appendChild(clonedComponent);
      document.body.appendChild(tempDiv);

      // Screenshot the isolated component
      const canvas = await html2canvas(tempDiv, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight
      });

      // Clean up
      document.body.removeChild(tempDiv);

      component.dataURL = canvas.toDataURL('image/png', 0.95);
      console.log('Successfully captured component image, size:', canvas.width, 'x', canvas.height);
    } catch (err) {
      console.warn('Failed to capture component:', err);
      // Continue without this component's image
    }
  }
}

/**
 * Create hybrid HTML with embedded component images
 */
function createHybridHTML(
  messageContainer: HTMLElement,
  foundComponents: ComponentCapture[],
  cleanedContent: string
): string {
  // Clone the container and replace components with images
  const clonedContainer = messageContainer.cloneNode(true) as HTMLElement;

  // Remove the copy button
  const copyButton = clonedContainer.querySelector('.copy-button');
  if (copyButton) {
    copyButton.remove();
  }

  // Replace custom components with images in the clone
  foundComponents.forEach(({ element, dataURL }) => {
    if (dataURL) {
      // Find all matching elements in the clone and replace the right one
      const originalElements = Array.from(messageContainer.querySelectorAll('*'));
      const clonedElements = Array.from(clonedContainer.querySelectorAll('*'));

      const originalIndex = originalElements.indexOf(element);
      if (originalIndex >= 0 && originalIndex < clonedElements.length) {
        const clonedElement = clonedElements[originalIndex];

        const img = document.createElement('img');
        img.src = dataURL;
        img.alt = 'Custom component';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';
        img.style.margin = '12px 0';
        img.style.borderRadius = '8px';
        img.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';

        // Replace the component with the image
        if (clonedElement.parentNode) {
          clonedElement.parentNode.replaceChild(img, clonedElement);
          console.log('Replaced component with image');
        }
      }
    }
  });

  // Get the final HTML content
  const htmlContent = clonedContainer.innerHTML;

  // Create complete HTML document
  return createHTMLDocument(htmlContent);
}

/**
 * Create a complete HTML document with proper structure and styling
 */
function createHTMLDocument(content: string): string {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { 
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: none;
      margin: 0;
      padding: 20px;
    }
    .prose { max-width: none; }
    h1, h2, h3 { color: #1a1a1a; font-weight: bold; }
    h1 { font-size: 2em; margin: 24px 0 16px 0; }
    h2 { font-size: 1.5em; margin: 20px 0 12px 0; }
    h3 { font-size: 1.17em; margin: 16px 0 8px 0; }
    p { margin-bottom: 12px; }
    strong { font-weight: 600; }
    code { 
      background-color: #f5f5f5; 
      padding: 2px 4px; 
      border-radius: 3px; 
      font-family: monospace; 
    }
    pre { 
      background-color: #f8f8f8; 
      padding: 12px; 
      border-radius: 6px; 
      overflow: auto; 
      margin-bottom: 12px; 
    }
    ul, ol { margin-bottom: 12px; padding-left: 20px; }
    li { margin-bottom: 4px; }
    blockquote { 
      border-left: 4px solid #e0e0e0; 
      padding-left: 16px; 
      margin: 12px 0; 
      font-style: italic; 
      color: #666; 
    }
    table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
    th, td { border: 1px solid #ddd; padding: 8px; }
    th { background-color: #f5f5f5; font-weight: bold; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>
${content}
</body>
</html>
  `.trim();
}

/**
 * Fallback: Copy as basic HTML without component screenshots
 */
export async function copyAsBasicHTML(
  cleanedContent: string,
  markdownComponents: any
): Promise<boolean> {
  try {
    const ReactDOMServer = (await import('react-dom/server')).default;

    const htmlContent = ReactDOMServer.renderToStaticMarkup(
      React.createElement('div', {},
        React.createElement(Markdown, {
          remarkPlugins: [remarkGfm, remarkMath],
          rehypePlugins: [rehypeRaw, rehypeKatex],
          components: markdownComponents,
          skipHtml: false
        }, cleanedContent)
      )
    );

    const fullHTML = createHTMLDocument(htmlContent);

    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([fullHTML], { type: 'text/html' }),
      'text/plain': new Blob([cleanedContent], { type: 'text/plain' })
    });

    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (err) {
    console.error('Failed to copy as basic HTML:', err);
    return false;
  }
}

/**
 * Simple markdown-only copy fallback
 */
export async function copyAsMarkdown(content: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (err) {
    console.error('Failed to copy as markdown:', err);
    return false;
  }
}